import { Slot } from 'radix-ui'
import { type VariantProps, cva } from 'class-variance-authority'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

/**
 * A 20px pill. The `soft` variants use the per-mode semantic tints. Both modes get
 * their own values because a tint that reads on near-black routinely fails on
 * white -- see the soft token pairs in globals.css. `neutral` (the default) sits one
 * step below `surface-raised` so it still reads when placed on a raised plate.
 */
const badgeVariants = cva(
    cn(
        'inline-flex w-fit shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-full',
        'border border-transparent px-2 py-px text-xs font-medium leading-4',
        "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3"
    ),
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground',
                secondary: 'bg-secondary text-secondary-foreground',
                outline: 'border-border bg-card text-foreground',
                neutral: 'bg-surface-hover text-muted-foreground',
                success: 'bg-success-soft text-success-soft-foreground',
                warning: 'bg-warning-soft text-warning-soft-foreground',
                destructive: 'bg-destructive-soft text-destructive-soft-foreground',
                info: 'bg-info-soft text-info-soft-foreground',
                'success-solid': 'bg-success text-success-foreground',
                'warning-solid': 'bg-warning text-warning-foreground',
                'destructive-solid': 'bg-destructive text-destructive-foreground',
                'info-solid': 'bg-info text-info-foreground'
            }
        },
        defaultVariants: { variant: 'neutral' }
    }
)

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

function Badge({
    className,
    variant,
    asChild = false,
    ...props
}: ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
    const Component = asChild ? Slot.Root : 'span'
    return <Component data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
}

export { Badge, badgeVariants }
